import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitInquiry } from "@/lib/api";
import { buildWaLink } from "@/lib/constants";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  phone: z
    .string()
    .min(6, "Phone number is too short")
    .max(20, "Phone number is too long"),
  travel_month: z.string().optional(),
  travelers: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) =>
      v === undefined || v === "" ? undefined : Number(v)
    ),
  message: z.string().max(2000).optional(),
});

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default function InquiryForm({
  packageSlug,
  packageTitle,
  source = "website",
  variant = "stacked",
}) {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await submitInquiry({
        ...values,
        package_slug: packageSlug || null,
        package_title: packageTitle || null,
        source,
      });
      toast.success("Inquiry received — we'll be in touch within hours.");
      reset();
    } catch (e) {
      toast.error(
        "Couldn't submit right now. Please try WhatsApp or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-[#1F3A5F]/30 focus:border-[#1F3A5F] outline-none py-3 text-[#11233B] placeholder:text-[#5A6B82]/60 transition-colors";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="inquiry-form"
      className={`${variant === "boxed" ? "bg-white p-8 md:p-10 border border-[#1F3A5F]/10" : ""}`}
      noValidate
    >
      {packageTitle && (
        <p className="eyebrow mb-2">Inquire · {packageTitle}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
            Full Name
          </label>
          <input
            data-testid="inquiry-name"
            placeholder="Your name"
            {...register("name")}
            className={fieldClass}
          />
          {errors.name && (
            <p className="text-xs text-red-700 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
            Email
          </label>
          <input
            data-testid="inquiry-email"
            type="email"
            placeholder="you@example.com"
            {...register("email")}
            className={fieldClass}
          />
          {errors.email && (
            <p className="text-xs text-red-700 mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
            Phone / WhatsApp
          </label>
          <input
            data-testid="inquiry-phone"
            placeholder="+91 9XXXXXXXXX"
            {...register("phone")}
            className={fieldClass}
          />
          {errors.phone && (
            <p className="text-xs text-red-700 mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
              Travel Month
            </label>
            <select
              data-testid="inquiry-month"
              {...register("travel_month")}
              className={`${fieldClass} appearance-none cursor-pointer`}
            >
              <option value="">Pick a month</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
              Travelers
            </label>
            <input
              data-testid="inquiry-travelers"
              type="number"
              min={1}
              max={50}
              placeholder="2"
              {...register("travelers")}
              className={fieldClass}
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <label className="text-xs uppercase tracking-[0.2em] text-[#5A6B82]">
          Tell us a bit more
        </label>
        <textarea
          data-testid="inquiry-message"
          rows={3}
          placeholder="Preferred dates, route preferences, must-visit places..."
          {...register("message")}
          className={`${fieldClass} resize-none`}
        />
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          data-testid="inquiry-submit"
          className="inline-flex items-center justify-center gap-2 bg-[#1F3A5F] hover:bg-[#11233B] disabled:opacity-60 text-[#FAF4ED] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Sending
            </>
          ) : (
            <>
              Send Inquiry <ArrowRight size={14} strokeWidth={1.6} />
            </>
          )}
        </button>

        <a
          href={buildWaLink(
            packageTitle
              ? `Hi iExplore, I'd like more details on the "${packageTitle}" package.`
              : null
          )}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="inquiry-whatsapp"
          className="inline-flex items-center justify-center gap-2 border border-[#1F3A5F] text-[#1F3A5F] hover:bg-[#1F3A5F] hover:text-[#FAF4ED] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
        >
          Or WhatsApp Us
        </a>
      </div>

      <p className="mt-6 text-xs text-[#5A6B82]">
        We typically reply within a few hours. No spam — just real planning help.
      </p>
    </form>
  );
}
